import { Container } from 'pixi.js';
import { Signal } from 'typed-signals';
import { MainMenuSelectorBackgroundView } from './MainMenuSelectorBackgroundView';
import { MainMenuPageButtonView } from './MainMenuPageButtonView';
import { PageType } from './types';

type PageSelectorConfig = {
  buttons: { type: PageType; locked?: boolean; hasNotification?: boolean }[];
  activeButton?: PageType;
};

export class MainMenuPageSelectorView extends Container {
  public readonly onTabButtonClicked = new Signal<(type: PageType) => void>();

  private readonly background = new MainMenuSelectorBackgroundView().props({
    parent: this,
  });

  private readonly buttons: Partial<Record<PageType, MainMenuPageButtonView>> = {};

  private horizontalLayout = false;

  private activeButton?: PageType;

  constructor(config: PageSelectorConfig) {
    super();

    this.activeButton = config.activeButton;

    this.initButtons(config);
    this.refreshView();
  }

  private initButtons(config: PageSelectorConfig): void {
    const activeButton = config.activeButton ?? PageType.Home;

    config.buttons.forEach(({ type, locked, hasNotification }) => {
      const button = new MainMenuPageButtonView()
        .init(type, locked, hasNotification)
        .setActive(type === activeButton, true)
        .props({
          parent: this,
        });
      button.onClicked.connect(() => {
        this.onTabButtonClicked.emit(type);
      });
      this.buttons[type] = button;
    });
    this.background.setSelectorPosition(this.buttons[activeButton]?.x ?? 0, true);
  }

  public setActiveButton(activeType: PageType, skipAnimation = false): void {
    this.activeButton = activeType;
    Object.entries(this.buttons).forEach(([buttonType, button]) => {
      const active = (buttonType as PageType) === activeType;
      button.setActive(active, skipAnimation);
      if (active) this.background.setSelectorPosition(button.x, skipAnimation);
    });
  }

  public setHorizontalLayout(horizontal: boolean): void {
    this.horizontalLayout = horizontal;

    this.background.setHorizontalLayout(horizontal);
    Object.values(this.buttons).forEach((button) => {
      button.y = horizontal ? 110 : -40;
      button.setHorizontalLayout(horizontal);
    });

    this.refreshView();
  }

  public refreshView(): void {
    this.pivot.x = this.horizontalLayout ? 360 : 0;
    this.background.setHorizontalLayout(this.horizontalLayout);

    const buttons = Object.values(this.buttons);
    const buttonSpacing = this.horizontalLayout ? 190 : 280;
    const buttonOffsetX = this.horizontalLayout ? -80 : 0;

    this.background.setHorizontalSize(buttonSpacing * buttons.length + 240);

    const visibleButtons = buttons.reduce((a, { visible }): number => a + (visible ? 1 : 0), 0);

    let buttonX = buttonSpacing * (visibleButtons - 1) * -0.5 + buttonOffsetX;
    const buttonY = this.horizontalLayout ? 110 : -40;
    Object.values(this.buttons).forEach((button) => {
      button.props({
        x: buttonX,
        y: buttonY,
      });
      button.setHorizontalLayout(this.horizontalLayout);
      buttonX += buttonSpacing;
    });

    if (this.activeButton) this.setActiveButton(this.activeButton, true);
  }

  public toggleNotificationOnButton(page: PageType, value: boolean): void {
    const button = this.buttons[page];
    if (!button) return;

    button.toggleNotification(value);
  }

  public async hide(): Promise<void> {
    this.interactiveChildren = false;
    if (this.alpha !== 0) {
      await this.tweenTo({ alpha: 0 }, 200, 'sinIn', true).asyncOnComplete();
    }
  }

  public async show(): Promise<void> {
    if (this.alpha !== 1) {
      await this.tweenTo({ alpha: 1 }, 200, 'sinIn', true).asyncOnComplete();
    }
    this.interactiveChildren = true;
  }
}
